import _ from "lodash";
import {
  INotification,
  INotifications,
} from "us.common/reducers/Notification/Interfaces";
import {
  NotificationTriggerType,
  NotificationUpdateType,
} from "us.common/constants/Component/Notification";
import { i18n } from "us.helper";
import { isJsonString } from "./functions";

/**
 * @function
 * @description - Handles notification payload
 * @param previousState - Existing state
 * @param payload - API response data
 * @returns Merged or fresh notifications
 */

export const getNotificationPayload = (
  previousState: { scroll: boolean; data: Array<INotification> },
  payload: Array<INotification>,
): Array<INotification> => {
  const { scroll, data } = previousState;
  try {
    if (Array.isArray(payload)) {
      return scroll
        ? _.uniqBy([...data, ...(payload ?? [])], "notificationId")
        : payload ?? [];
    } else {
      return data;
    }
  } catch (e) {
    return data;
  }
};

/**
 * @function
 * @description - Remove notifications
 * @param {INotifications} previousState - Previous notification state.
 * @param payload - Notification id and action type.
 * @returns Object with notifications and unread count.
 */
export const removeNotification = (
  previousState: INotifications,
  payload: any,
): { data: Array<INotification>; unReadCount: number } => {
  const { data, unReadCount } = previousState;
  const { notificationId, type } = payload ?? {};
  try {
    if (type == NotificationUpdateType.DELETE) {
      if (notificationId !== -1) {
        return {
          data: data.filter(
            (notification: INotification) =>
              notification?.notificationId !== notificationId,
          ),
          unReadCount: Number(payload?.data?.unRead),
        };
      } else {
        return { data: [], unReadCount: 0 };
      }
    } else {
      const updatedNotifications = data.map((notification: INotification) => {
        return notification.notificationId == notificationId
          ? { ...notification, isSeen: true }
          : notification;
      });
      return {
        data: updatedNotifications,
        unReadCount: Number(payload?.data?.unRead),
      };
    }
  } catch (e) {
    return { data, unReadCount };
  }
};

/**
 * @function
 * @description - Add or remove pending notification events.
 * @param {Array} pendingEvents - Pending notifications
 * @param {Number} eventId - Notification id
 * @returns Updated pending notifications
 */
export const updatePendingEvents = (
  pendingEvents: Array<any>,
  eventId: string,
  isInProgress: boolean,
): Array<any> => {
  try {
    if (eventId) {
      if (pendingEvents.includes(eventId) && !isInProgress) {
        return pendingEvents.filter((id: any) => id !== eventId);
      } else {
        return [...pendingEvents, eventId];
      }
    } else {
      return pendingEvents;
    }
  } catch (e) {
    return pendingEvents;
  }
};

/**
 * @function
 * @description - Handle notification message
 * @param notifications - notifications list
 * @returns notifications list with updated message
 */

export const updateNotificationMessage = (
  notifications: Array<INotification>,
): Array<INotification> => {
  try {
    if (Array.isArray(notifications)) {
      return notifications.map((notification) => {
        let updatedTitle = notification.title || "";
        let updatedMessage = notification.message || "";

        if (/^[A-Z_]+$/.test(updatedTitle) && notification.title) {
          updatedTitle = i18n.t(
            `US.COMMON:NOTIFICATIONS.${notification.title}`,
          );
        }

        if (/^[A-Z_]+$/.test(updatedMessage) && notification.message) {
          // If the message is transalation key

          updatedMessage = i18n.t(
            `US.COMMON:NOTIFICATIONS.${notification.message}`,
          );
        } else if (isJsonString(updatedMessage)) {
          // If the message is a JSON object string
          const parsedContent = JSON.parse(updatedMessage);
          if (
            notification?.target ===
            NotificationTriggerType.ON_RECEIVE_CREDITOR_PORTAL_MESSAGE
          ) {
            const caseHandlerText = parsedContent?.data?.caseHandler
              ? `(${i18n.t(`US.COMMON:NOTIFICATIONS.CASE_HANDLER`, {
                  caseHandler: parsedContent?.data?.caseHandler || "",
                })})`
              : "";
            if (parsedContent?.title) {
              updatedTitle = i18n.t(
                `US.COMMON:NOTIFICATIONS.${parsedContent.title}`,
              );
            } else {
              updatedTitle = i18n.t(
                `US.COMMON:NOTIFICATIONS.NEW_MESSAGE_RECEIVED`,
              );
            }
            if (parsedContent?.message) {
              updatedMessage = i18n.t(
                `US.COMMON:NOTIFICATIONS.${parsedContent.message}`,
                {
                  caseNo: parsedContent?.data?.caseNo || "",
                  caseHandler: caseHandlerText || "",
                },
              );
            } else {
              updatedMessage = i18n.t(
                `US.COMMON:NOTIFICATIONS.ACTION_REQUIRED_FOR_CASE`,
                {
                  caseNo: parsedContent?.data?.caseNo || "",
                  caseHandler: caseHandlerText || "",
                },
              );
            }
          } else {
            updatedMessage =
              i18n.t("US.COMMON:NOTIFICATIONS.BULK_ACTIVITY_EXECUTION") +
              ` ${parsedContent?.status || ""}`;
          }
        }

        return {
          ...notification,
          message: updatedMessage,
          title: updatedTitle,
        };
      });
    } else {
      return notifications;
    }
  } catch (e) {
    console.error("Error updating notifications:", e);
    return notifications;
  }
};
